import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Loader2, Users, Folder, TrendingUp, TrendingDown, Minus, Award, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_STATS = {
  total_employees: 156,
  total_spaces: 8,
  compliance_rate: 87,
  compliance_change: 3,
  active_requirements: 42,
  completed_this_month: 28,
};

const StatCard = ({ icon: Icon, label, value, suffix, change, color, onClick }) => {
  const getTrendIcon = () => {
    if (change > 0) return <TrendingUp className="h-3 w-3 text-green-500" />;
    if (change < 0) return <TrendingDown className="h-3 w-3 text-red-500" />;
    return <Minus className="h-3 w-3 text-gray-400" />;
  };

  return (
    <div
      onClick={onClick}
      className={cn(
        "p-3 rounded-lg border bg-white transition-all",
        onClick && "cursor-pointer hover:shadow-md"
      )}
    >
      <div className="flex items-start justify-between mb-2">
        <div className={cn("p-1.5 rounded-lg", color)}>
          <Icon className="h-4 w-4" />
        </div>
        {change !== undefined && (
          <div className="flex items-center gap-0.5">
            {getTrendIcon()}
            <span className={cn(
              "text-[10px] font-medium",
              change > 0 ? "text-green-600" : change < 0 ? "text-red-600" : "text-gray-500"
            )}>
              {change > 0 ? '+' : ''}{change}%
            </span>
          </div>
        )}
      </div>
      <div className="flex items-baseline gap-0.5">
        <span className="text-xl font-bold text-gray-900">{value}</span>
        {suffix && <span className="text-sm text-gray-500">{suffix}</span>}
      </div>
      <p className="text-[10px] text-gray-500 mt-0.5">{label}</p>
    </div>
  );
};

export function CompanyStatsTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      setStats(MOCK_STATS);
      setLoading(false);
      return;
    }

    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadStats = async () => {
      try {
        setLoading(true);

        // Get employee count
        const { count: employeeCount } = await supabase
          .from('user_profiles')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', company.id);

        // Get spaces count
        const { count: spacesCount } = await supabase
          .from('projects')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', company.id)
          .eq('is_archived', false);

        // Get compliance data using dashboard RPC
        const { data: dashData } = await supabase.rpc('get_dashboard_data_v2', {
          p_company_id: company.id,
          p_user_id: null,
          p_include_archived: false,
          p_limit: 1,
          p_offset: 0,
          p_search: null,
        });

        // Calculate overall compliance from projects
        const projects = dashData?.projects || [];
        const avgCompliance = projects.length > 0
          ? Math.round(projects.reduce((sum, p) => sum + (p.compliance_summary?.percentage || 0), 0) / projects.length)
          : 0;

        setStats({
          total_employees: employeeCount || 0,
          total_spaces: spacesCount || 0,
          compliance_rate: avgCompliance,
          compliance_change: 0, // Would need historical data
          active_requirements: dashData?.summary?.total_requirements || 0,
          completed_this_month: dashData?.summary?.compliant_count || 0,
        });
      } catch (error) {
        console.error('Failed to load company stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [company?.id, useMockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <div className="bg-gray-100 p-3 rounded-full mb-3">
          <TrendingUp className="h-6 w-6 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-900">No data available</p>
      </div>
    );
  }

  const isLarge = size === 'large' || size === 'xl';

  return (
    <div className="h-full">
      <div className={cn(
        "grid gap-2 h-full content-start",
        isLarge ? "grid-cols-2" : "grid-cols-2"
      )}>
        <StatCard
          icon={Users}
          label="Team Members"
          value={stats.total_employees}
          color="bg-blue-100 text-blue-600"
          onClick={() => navigate('/company-team')}
        />
        <StatCard
          icon={Folder}
          label="Active Spaces"
          value={stats.total_spaces}
          color="bg-purple-100 text-purple-600"
          onClick={() => navigate('/spaces')}
        />
        <StatCard
          icon={Award}
          label="Compliance Rate"
          value={stats.compliance_rate}
          suffix="%"
          change={stats.compliance_change}
          color="bg-emerald-100 text-emerald-600"
        />
        <StatCard
          icon={CheckCircle}
          label="Completed"
          value={stats.completed_this_month}
          color="bg-amber-100 text-amber-600"
        />
      </div>
    </div>
  );
}
